import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const Home = () => {
  return (
    <div className="w-full">
      {/* Hero Section */}
      <section className="relative bg-purple-50/40 overflow-hidden border-b border-purple-100">
        <div className="max-w-6xl mx-auto px-4 py-20 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center gap-12">
          <div className="flex-1 space-y-6">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="inline-block px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-semibold tracking-wide uppercase"
            >
              The Can Do Method
            </motion.div>
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="font-serif text-4xl md:text-5xl lg:text-6xl text-purple-900 leading-tight"
            >
              Focusing on what your baby <span className="italic">can do</span> today.
            </motion.h1>
            <p className="text-lg text-gray-700 max-w-lg leading-relaxed">
              Strengths-based monitoring for birth to age 5. We help you understand your child’s brain development by building on their existing abilities, reducing anxiety and providing clear, practical next steps.
            </p>
            <div className="pt-4 flex flex-wrap gap-4">
              <Link to="/services" className="bg-purple-800 text-white px-8 py-3 rounded-md hover:bg-purple-900 transition-all shadow-md">
                Our Packages
              </Link>
              <Link to="/about" className="bg-white text-purple-800 border border-purple-200 px-8 py-3 rounded-md hover:bg-purple-50 transition-colors">
                The Can Do Philosophy
              </Link>
            </div>
          </div>
          <div className="flex-1 relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-[4/3] max-w-lg mx-auto border-8 border-white">
              <img 
                src="https://images.unsplash.com/photo-1544126592-807daa2b565b?q=80&w=2070&auto=format&fit=crop" 
                alt="Confident parent and child" 
                className="object-cover w-full h-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* The Core Message */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <SafeIcon icon={FiIcons.FiTarget} className="text-4xl text-purple-600 mx-auto mb-8" />
          <h2 className="font-serif text-3xl md:text-4xl text-purple-900 mb-8">A Strengths-Based Approach</h2>
          <div className="space-y-6 text-gray-600 text-lg leading-relaxed">
            <p>
              Standard developmental tools often focus on what a child is <em>not</em> yet doing, which can leave parents feeling vulnerable and stressed.
            </p>
            <p className="font-serif text-2xl text-purple-800 italic">
              The Can Do Method reverses this.
            </p>
            <p>
              By identifying what your child is currently achieving and what they are <strong>close to doing</strong>, we maximize early brain development while increasing your confidence as a parent.
            </p>
          </div>
        </div>
      </section>

      {/* Why Can Do? */}
      <section className="py-20 bg-stone-50 border-y border-stone-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-12">
            <div className="space-y-4">
              <div className="w-12 h-12 bg-purple-800 text-white rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiIcons.FiZap} className="text-xl" />
              </div>
              <h3 className="font-serif text-xl font-semibold text-purple-900">Maximize Potential</h3>
              <p className="text-gray-600">Targeted activities based on what your child is nearly ready for to boost brain development.</p>
            </div>
            <div className="space-y-4">
              <div className="w-12 h-12 bg-purple-800 text-white rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiIcons.FiShield} className="text-xl" />
              </div>
              <h3 className="font-serif text-xl font-semibold text-purple-900">Reduce Anxiety</h3>
              <p className="text-gray-600">Replace stressful checklists with reassuring, evidence-based observations of current ability.</p>
            </div>
            <div className="space-y-4">
              <div className="w-12 h-12 bg-purple-800 text-white rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiIcons.FiMap} className="text-xl" />
              </div>
              <h3 className="font-serif text-xl font-semibold text-purple-900">Practical Next Steps</h3>
              <p className="text-gray-600">Know exactly what to do next to support your child's natural progression.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Local Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center gap-12">
            <div className="md:w-1/2">
               <img 
                src="https://images.unsplash.com/photo-1510154221590-ff63e90a136f?q=80&w=2070&auto=format&fit=crop" 
                alt="Local community support" 
                className="rounded-2xl shadow-lg border border-stone-200"
              />
            </div>
            <div className="md:w-1/2 space-y-6">
              <h2 className="font-serif text-3xl text-purple-900 font-semibold">Local, Grounded Presence</h2>
              <p className="text-gray-600 leading-relaxed">
                Based in Saddleworth, I work directly with families across Greater Manchester, Lancashire, Yorkshire, and Derbyshire. I bring 10+ years of Health Visiting experience into your home, providing a calm authority that generic apps cannot replicate.
              </p>
              <div className="flex items-center gap-3 text-purple-800 font-medium">
                <SafeIcon icon={FiIcons.FiNavigation} />
                <span>Serving Saddleworth & Surrounding Areas</span>
              </div>
              <Link to="/contact" className="inline-block border-b-2 border-purple-800 pb-1 text-purple-900 font-semibold hover:text-purple-700 transition-colors">
                Check service availability in your postcode
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Simple CTA */}
      <section className="py-20 bg-purple-900 text-white text-center">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="font-serif text-3xl mb-6 italic">Building on what's already there.</h2>
          <p className="text-purple-100 text-lg mb-10">
            Let's start your child's developmental journey with the Can Do Method. Professional, practical, and personally delivered.
          </p>
          <Link to="/contact" className="bg-white text-purple-950 px-10 py-4 rounded-md font-semibold hover:bg-stone-100 transition-all inline-block shadow-lg text-purple-950">
            Book a Consultation
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;